// src/admin/sections/JournalPromptsSection.jsx
//
// Ticket 58 (admin spec section 11). Admin: Journal Prompts. The list a reader
// picks from when they journal a book (ProfileBlocks.jsx reads the `prompts`
// table, active rows, in sort_order). Add, edit, reorder, retire or activate,
// and delete.
//
// TWO KINDS OF ROW
//   preset   an ordinary prompt ("a book that made me cry"). Add, edit, move,
//            retire, delete.
//   freeform "type your own", and
//   talk     "I just want to talk about a book". The journal looks these two up
//            by kind, and the journal spec requires both, so they are shown as
//            fixed entries: editable (their label, emoji and colour), never
//            movable, retired or deleted. The database says the same
//            (prompts_guard in 20261024100000_ticket58_journal_prompts.sql), so a
//            stale screen or a hand-written query meets the same rule.
//
// "IN USE" IS A NUMBER THE DATABASE GIVES, NOT A READ OF ENTRIES
//   Only a reflection's owner can read it, and an admin must not read private
//   writing to count it. admin_prompt_usage() returns one number per prompt and
//   nothing else. While that number is unknown the Delete button says so and
//   stays off: "not in use" is never guessed.
//
// VIEW (ticket 59, decided 24 Oct)
//   A prompt in use shows View (N entries) in place of the greyed-out Delete; Delete
//   stays for a prompt nobody has used. The two fixed entries have no Delete, so they
//   get View when in use too. View lists every reader who used the prompt, private
//   entries included: a username linking to their profile, and how many of their
//   entries use it. It comes from admin_prompt_readers(), which returns a name and a
//   number and nothing from an entry: no words, no book, no typed-in label, no title.
//   The journal's wording ("never with your name on it") is left alone on purpose.
//
// PAST ENTRIES DON'T CHANGE
//   A reflection keeps its own copy of the prompt's label, emoji and colour, so
//   editing or retiring a prompt here changes what readers are offered from now
//   on, not what they wrote.
//
// EVERY WRITE LOOKS AT THE DATABASE'S ANSWER. The list changes only after the
// row (or the reorder's reply) comes back; a refused or empty write is reported,
// never shown as done. The audit line is written after, never before.

import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../lib/supabase';
import { logAudit } from '../lib/auditLog';

const COLUMNS = 'id, slug, kind, label, emoji, color, sort_order, is_active';

export const LABEL_MAX = 80;
export const EMOJI_MAX = 16;
export const DEFAULT_COLOUR = '#d4a84b';
// A preset's sort_order is its place times 10; the two fixed entries sit at 900 and 910.
export const FIXED_FROM = 900;
export const MAX_PRESETS = 89;

const TONE = {
  ok:   { bg: 'var(--feedback-success-bg)', color: 'var(--feedback-success)', border: 'var(--feedback-success)' },
  warn: { bg: 'var(--feedback-warning-bg)', color: 'var(--feedback-warning-ink)', border: 'var(--feedback-warning-border)' },
  bad:  { bg: 'var(--feedback-error-bg)', color: 'var(--feedback-error)', border: 'var(--feedback-error)' },
};

// ── pure helpers (the guard runs these) ──────────────────────────────────────

export function isHexColour(s) {
  return typeof s === 'string' && /^#[0-9a-fA-F]{6}$/.test(s);
}

// What the form may send, or why it can't: { ok, label, emoji, color } or { ok:false, text }.
export function checkPromptFields({ label, emoji, color }) {
  const l = (label ?? '').trim();
  const e = (emoji ?? '').trim();
  if (!l) return { ok: false, text: 'Write the prompt first.' };
  if (l.length > LABEL_MAX) return { ok: false, text: `Keep the prompt to ${LABEL_MAX} characters or fewer.` };
  if (!e) return { ok: false, text: 'Add an emoji: the journal shows one beside every prompt.' };
  if (e.length > EMOJI_MAX) return { ok: false, text: 'That is more than one emoji.' };
  if (!isHexColour(color)) return { ok: false, text: 'The colour must look like #d4a84b: a # and six letters or digits.' };
  return { ok: true, label: l, emoji: e, color: color.toLowerCase() };
}

// 'A book I couldn't put down!' -> 'a-book-i-couldn-t-put-down', and -2, -3 ... if taken.
export function slugFor(label, taken = []) {
  const base = (label ?? '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40).replace(/-+$/g, '') || 'prompt';
  const used = new Set(taken);
  if (!used.has(base)) return base;
  let n = 2;
  while (used.has(`${base}-${n}`)) n += 1;
  return `${base}-${n}`;
}

export function splitPrompts(rows) {
  const list = Array.isArray(rows) ? rows : [];
  const byOrder = (a, b) => (a.sort_order - b.sort_order) || (a.id - b.id);
  return {
    presets: list.filter(r => r.kind === 'preset').sort(byOrder),
    fixed:   list.filter(r => r.kind !== 'preset').sort(byOrder),
  };
}

// New presets go last among the presets, a step of 10 after the highest one.
export function nextPresetOrder(presets) {
  const top = presets.reduce((m, r) => Math.max(m, Number(r.sort_order) || 0), 0);
  return top + 10;
}

// Move one id up (-1) or down (+1) in a list of ids. A move off either end changes nothing.
export function movePreset(ids, id, dir) {
  const i = ids.indexOf(id);
  const j = i + dir;
  if (i < 0 || j < 0 || j >= ids.length) return ids;
  const next = ids.slice();
  [next[i], next[j]] = [next[j], next[i]];
  return next;
}

// admin_prompt_usage() rows -> Map(prompt id -> uses). null when the count wasn't read.
export function usageById(rows) {
  if (!Array.isArray(rows)) return null;
  return new Map(rows.map(r => [Number(r.prompt_id), Number(r.uses)]));
}

// What Delete may do for one row: 'none' (fixed entries have no Delete), 'unknown',
// 'in_use' (with the count) or 'ok'.
export function deleteState(row, usage) {
  if (row.kind !== 'preset') return { state: 'none' };
  if (!usage || !usage.has(Number(row.id))) return { state: 'unknown' };
  const uses = usage.get(Number(row.id));
  return uses > 0 ? { state: 'in_use', uses } : { state: 'ok', uses: 0 };
}

// What View may do for one row: only when the count says someone used it.
export function viewState(row, usage) {
  if (!usage || !usage.has(Number(row.id))) return { state: 'unknown' };
  const uses = usage.get(Number(row.id));
  return uses > 0 ? { state: 'ok', uses } : { state: 'none', uses: 0 };
}

export function viewText(uses) {
  return `View (${uses} ${uses === 1 ? 'entry' : 'entries'})`;
}

// admin_prompt_readers() rows -> { readers, entries }.
export function readersTotals(rows) {
  const list = Array.isArray(rows) ? rows : [];
  return { readers: list.length, entries: list.reduce((n, r) => n + Number(r.uses || 0), 0) };
}

export function usesText(uses) {
  return uses === 1 ? 'used by 1 entry' : `used by ${uses} entries`;
}

// What the database said when a write was refused, in words.
export function describeSaveError(error) {
  if (error?.code === '23505') return 'Not saved: a prompt with that name already exists.';
  if (error?.code === '23514') return `Not saved: ${error.message}`;
  if (error?.code === '23503') return `Not deleted: ${error.message}`;
  return `Not saved: ${error?.message || 'the database refused it'}.`;
}

// ── small parts ──────────────────────────────────────────────────────────────

function Btn({ children, onClick, disabled, primary, danger, isMobile, title, label }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      aria-label={label}
      style={{
        padding: isMobile ? '0 14px' : '6px 12px', minHeight: isMobile ? 44 : undefined,
        background: primary ? 'var(--brand-forest)' : danger ? 'var(--feedback-error)' : 'var(--brand-white)',
        color: primary || danger ? 'var(--brand-white)' : 'var(--neutral-600)',
        border: primary || danger ? 'none' : '1px solid var(--brand-sand)',
        borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-xs)', fontWeight: 700,
        cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.55 : 1, fontFamily: 'var(--font-body)',
      }}
    >
      {children}
    </button>
  );
}

function Pill({ children, tone }) {
  const t = TONE[tone] || { bg: '#f5f0e8', color: 'var(--neutral-600)', border: 'var(--brand-sand)' };
  return (
    <span style={{ fontSize: 'var(--font-size-xxs)', fontWeight: 700, padding: '2px 8px', borderRadius: 'var(--radius-md)', background: t.bg, color: t.color, border: `1px solid ${t.border}`, whiteSpace: 'nowrap' }}>
      {children}
    </span>
  );
}

function Note({ result }) {
  if (!result) return null;
  const t = TONE[result.tone] || TONE.warn;
  return (
    <div role="status" style={{ marginTop: 8, padding: '8px 12px', borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-sm)', fontWeight: 600, background: t.bg, color: t.color, border: `1px solid ${t.border}` }}>
      {result.text}
    </div>
  );
}

const inputStyle = {
  boxSizing: 'border-box', padding: '8px 10px', border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-md)',
  fontSize: 'var(--font-size-sm)', fontFamily: 'var(--font-body)', background: 'var(--brand-white)', color: 'var(--neutral-800)',
};

// The three fields, shared by Add and Edit.
function Fields({ value, onChange, isMobile, idPrefix }) {
  return (
    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
      <label style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: '1 1 260px', fontSize: 'var(--font-size-xs)', fontWeight: 700, color: 'var(--neutral-500)' }}>
        Prompt
        <input
          id={`${idPrefix}-label`}
          type="text"
          value={value.label}
          maxLength={LABEL_MAX}
          onChange={e => onChange({ ...value, label: e.target.value })}
          placeholder="a book that made me cry"
          style={{ ...inputStyle, minHeight: isMobile ? 44 : undefined }}
        />
      </label>
      <label style={{ display: 'flex', flexDirection: 'column', gap: 4, width: 90, fontSize: 'var(--font-size-xs)', fontWeight: 700, color: 'var(--neutral-500)' }}>
        Emoji
        <input
          id={`${idPrefix}-emoji`}
          type="text"
          value={value.emoji}
          maxLength={EMOJI_MAX}
          onChange={e => onChange({ ...value, emoji: e.target.value })}
          style={{ ...inputStyle, minHeight: isMobile ? 44 : undefined, textAlign: 'center' }}
        />
      </label>
      <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 'var(--font-size-xs)', fontWeight: 700, color: 'var(--neutral-500)' }}>
        Colour
        <span style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <input
            type="color"
            aria-label="Pick a colour"
            value={isHexColour(value.color) ? value.color.toLowerCase() : DEFAULT_COLOUR}
            onChange={e => onChange({ ...value, color: e.target.value })}
            style={{ width: 44, height: isMobile ? 44 : 36, padding: 2, border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-md)', background: 'var(--brand-white)' }}
          />
          <input
            id={`${idPrefix}-colour`}
            type="text"
            value={value.color}
            maxLength={7}
            onChange={e => onChange({ ...value, color: e.target.value })}
            aria-label="Colour as #rrggbb"
            style={{ ...inputStyle, width: 96, minHeight: isMobile ? 44 : undefined, fontFamily: 'monospace' }}
          />
        </span>
      </label>
    </div>
  );
}

function Chip({ row }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
      <span aria-hidden="true" style={{ width: 12, height: 12, borderRadius: '50%', background: isHexColour(row.color) ? row.color : 'var(--neutral-300)', border: '1px solid rgba(0,0,0,0.12)', flexShrink: 0 }} />
      <span style={{ fontSize: 'var(--font-size-base)', color: 'var(--neutral-800)' }}>{row.emoji} {row.label}</span>
    </span>
  );
}

// Who used a prompt. Read when the panel opens; a failed read says so and is not an empty list.
function PromptReaders({ row, uses }) {
  const [state, setState] = useState({ loading: true, error: null, rows: null });
  useEffect(() => {
    let live = true;
    (async () => {
      const { data, error } = await supabase.rpc('admin_prompt_readers', { p_prompt_id: row.id });
      if (!live) return;
      if (error) { console.error('[Journal Prompts] readers failed:', error.message); setState({ loading: false, error: error.message, rows: null }); return; }
      setState({ loading: false, error: null, rows: Array.isArray(data) ? data : [] });
    })();
    return () => { live = false; };
  }, [row.id]);

  const totals = readersTotals(state.rows);
  return (
    <div data-readers-panel style={{ marginTop: 10, padding: '10px 12px', background: 'var(--brand-paper)', border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-md)' }}>
      <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-500)', marginBottom: 8, lineHeight: 1.5 }}>
        Everyone who used this prompt, private entries included. Only a name and a number: nothing from inside an entry.
      </div>
      {row.kind === 'talk' && (
        <div data-readers-talk-note style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-500)', marginBottom: 8, lineHeight: 1.5 }}>
          This list also includes readers who never chose this prompt. Before 27 Oct, &ldquo;+ add another entry&rdquo; saved the next entry as this prompt, and the homepage journal offer still does.
        </div>
      )}
      {state.loading && <span role="status" style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-400)' }}>Loading…</span>}
      {state.error && (
        <div role="alert" data-readers-error style={{ fontSize: 'var(--font-size-sm)', color: TONE.bad.color, fontWeight: 600 }}>
          Couldn&apos;t read who used this prompt: {state.error}. That is not the same as nobody.
        </div>
      )}
      {state.rows && state.rows.length === 0 && <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-500)' }}>No reader came back for this prompt.</span>}
      {state.rows && state.rows.length > 0 && (
        <>
          <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 4 }}>
            {state.rows.map((r, i) => (
              <li key={`${r.username || 'none'}-${i}`} data-reader-row style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-700)' }}>
                {r.username
                  ? <a href={`/u/${encodeURIComponent(r.username)}`} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--neutral-800)', fontWeight: 700 }}>@{r.username}</a>
                  : <span style={{ fontWeight: 700 }}>a reader with no username</span>}
                {' '}· {Number(r.uses)} {Number(r.uses) === 1 ? 'entry' : 'entries'}
              </li>
            ))}
          </ul>
          <div data-readers-total style={{ marginTop: 8, fontSize: 'var(--font-size-xs)', color: 'var(--neutral-500)' }}>
            {totals.readers} {totals.readers === 1 ? 'reader' : 'readers'} · {totals.entries} {totals.entries === 1 ? 'entry' : 'entries'}
            {totals.entries !== uses && <strong data-readers-mismatch style={{ color: TONE.warn.color }}> · the screen counted {uses}, so the list changed since you opened it: reload to check</strong>}
          </div>
        </>
      )}
    </div>
  );
}

function PromptRow({ row, index, count, usage, isMobile, busy, result, editing, confirming,
  onMove, onStartEdit, onCancelEdit, onSaveEdit, onToggle, onAskDelete, onCancelDelete, onDelete }) {
  const fixed = row.kind !== 'preset';
  const [draft, setDraft] = useState({ label: row.label, emoji: row.emoji || '', color: row.color || DEFAULT_COLOUR });
  useEffect(() => { if (editing) setDraft({ label: row.label, emoji: row.emoji || '', color: row.color || DEFAULT_COLOUR }); }, [editing, row.label, row.emoji, row.color]);
  const checked = checkPromptFields(draft);
  const changed = draft.label.trim() !== row.label || draft.emoji.trim() !== (row.emoji || '') || draft.color.toLowerCase() !== (row.color || '').toLowerCase();
  const del = deleteState(row, usage);
  const view = viewState(row, usage);
  const [viewing, setViewing] = useState(false);

  return (
    <div data-prompt={row.id} data-kind={row.kind} style={{ background: 'var(--brand-white)', border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-lg)', padding: isMobile ? 12 : 14, marginBottom: 10 }}>
      {editing ? (
        <div>
          <Fields value={draft} onChange={setDraft} isMobile={isMobile} idPrefix={`edit-${row.id}`} />
          {!checked.ok && changed && <div role="status" style={{ marginTop: 8, fontSize: 'var(--font-size-xs)', fontWeight: 700, color: TONE.warn.color }}>{checked.text}</div>}
          <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
            <Btn primary isMobile={isMobile} disabled={busy || !checked.ok || !changed} onClick={() => onSaveEdit(row, checked)}>{busy ? 'Saving…' : 'Save'}</Btn>
            <Btn isMobile={isMobile} disabled={busy} onClick={onCancelEdit}>Cancel</Btn>
          </div>
        </div>
      ) : (
        <>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', minWidth: 0 }}>
              <Chip row={row} />
              {fixed && <Pill>fixed</Pill>}
              <Pill tone={row.is_active ? 'ok' : 'warn'}>{row.is_active ? 'Showing' : 'Retired'}</Pill>
              <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-500)' }} data-uses={usage && usage.has(Number(row.id)) ? usage.get(Number(row.id)) : 'unknown'}>
                {usage && usage.has(Number(row.id)) ? usesText(usage.get(Number(row.id))) : 'count unavailable'}
              </span>
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {!fixed && (
                <>
                  <Btn isMobile={isMobile} disabled={busy || index === 0} onClick={() => onMove(row, -1)} label={`Move ${row.label} up`} title="Move up">↑</Btn>
                  <Btn isMobile={isMobile} disabled={busy || index === count - 1} onClick={() => onMove(row, 1)} label={`Move ${row.label} down`} title="Move down">↓</Btn>
                </>
              )}
              <Btn isMobile={isMobile} disabled={busy} onClick={() => onStartEdit(row)}>Edit</Btn>
              {!fixed && <Btn isMobile={isMobile} disabled={busy} onClick={() => onToggle(row)}>{row.is_active ? 'Retire' : 'Activate'}</Btn>}
              {del.state === 'ok' && <Btn danger isMobile={isMobile} disabled={busy} onClick={() => onAskDelete(row)}>Delete</Btn>}
              {view.state === 'ok' && <Btn isMobile={isMobile} onClick={() => setViewing(v => !v)}>{viewing ? 'Hide readers' : viewText(view.uses)}</Btn>}
              {del.state === 'unknown' && <Btn isMobile={isMobile} disabled title="The use count could not be read, so Delete stays off">Delete (count unavailable)</Btn>}
            </div>
          </div>
          {viewing && view.state === 'ok' && <PromptReaders row={row} uses={view.uses} />}
          {confirming && del.state === 'ok' && (
            <div role="alertdialog" aria-label="Confirm delete" style={{ marginTop: 10, padding: '10px 12px', background: TONE.bad.bg, border: `1px solid ${TONE.bad.border}`, borderRadius: 'var(--radius-md)' }}>
              <div style={{ fontSize: 'var(--font-size-sm)', color: TONE.bad.color, fontWeight: 600, marginBottom: 8 }}>
                Delete this prompt for good? No journal entry uses it. This can&apos;t be undone.
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <Btn danger isMobile={isMobile} disabled={busy} onClick={() => onDelete(row)}>{busy ? 'Deleting…' : 'Yes, delete it'}</Btn>
                <Btn isMobile={isMobile} disabled={busy} onClick={onCancelDelete}>Keep it</Btn>
              </div>
            </div>
          )}
        </>
      )}
      <Note result={result} />
    </div>
  );
}

// ── the screen ───────────────────────────────────────────────────────────────

export default function JournalPromptsSection({ isMobile }) {
  const [rows, setRows] = useState([]);
  const [usage, setUsage] = useState(null);            // Map, or null when the counts weren't read
  const [usageError, setUsageError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [busyIds, setBusyIds] = useState(() => new Set());
  const [results, setResults] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const [form, setForm] = useState({ label: '', emoji: '', color: DEFAULT_COLOUR });
  const [adding, setAdding] = useState(false);
  const [addResult, setAddResult] = useState(null);
  const [reordering, setReordering] = useState(false);

  const setBusy = (id, on) => setBusyIds(prev => { const n = new Set(prev); if (on) n.add(id); else n.delete(id); return n; });
  const setResult = (id, r) => setResults(prev => ({ ...prev, [id]: r }));
  const patchRow = (id, patch) => setRows(prev => prev.map(r => (r.id === id ? { ...r, ...patch } : r)));

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    const { data, error } = await supabase
      .from('prompts')
      .select(COLUMNS)
      .order('sort_order', { ascending: true })
      .order('id', { ascending: true })
      .limit(500);
    if (error) {
      setLoadError(error.message);
      setLoading(false);
      return;
    }
    setRows(data || []);
    // The use counts come from the database function. If they can't be read the
    // screen says so and keeps Delete off; it never assumes a prompt is unused.
    const { data: usageRows, error: usageErr } = await supabase.rpc('admin_prompt_usage');
    if (usageErr) {
      setUsage(null);
      setUsageError(usageErr.message);
    } else {
      setUsage(usageById(usageRows));
      setUsageError(null);
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const { presets, fixed } = splitPrompts(rows);
  const showing = [...presets, ...fixed].filter(r => r.is_active);

  async function addOne() {
    const checked = checkPromptFields(form);
    if (!checked.ok) { setAddResult({ tone: 'warn', text: checked.text }); return; }
    if (presets.length >= MAX_PRESETS) { setAddResult({ tone: 'warn', text: `Not added: the list is full at ${MAX_PRESETS} prompts. Delete one that nothing uses first.` }); return; }
    setAdding(true);
    setAddResult(null);
    const { data: saved, error } = await supabase
      .from('prompts')
      .insert({
        slug: slugFor(checked.label, rows.map(r => r.slug)),
        kind: 'preset',
        label: checked.label,
        emoji: checked.emoji,
        color: checked.color,
        sort_order: nextPresetOrder(presets),
        is_active: true,
      })
      .select(COLUMNS)
      .single();
    if (error || !saved) {
      setAddResult({ tone: 'bad', text: describeSaveError(error) });
      setAdding(false);
      return;
    }
    await logAudit('create', 'journal_prompt', `Added journal prompt #${saved.id}: ${saved.label}`, saved.id);
    setRows(prev => [...prev, saved]);
    setUsage(prev => (prev ? new Map(prev).set(Number(saved.id), 0) : prev));
    setForm({ label: '', emoji: '', color: DEFAULT_COLOUR });
    setAddResult({ tone: 'ok', text: 'Added at the end of the list. Readers see it now. Use the arrows to move it.' });
    setAdding(false);
  }

  async function saveEdit(row, checked) {
    setBusy(row.id, true);
    setResult(row.id, null);
    const { data: saved, error } = await supabase
      .from('prompts')
      .update({ label: checked.label, emoji: checked.emoji, color: checked.color })
      .eq('id', row.id)
      .select(COLUMNS);
    if (error) {
      setResult(row.id, { tone: 'bad', text: describeSaveError(error) });
      setBusy(row.id, false);
      return;
    }
    if (!saved?.length) {
      setResult(row.id, { tone: 'warn', text: 'Not saved: the prompt was not found. Someone may have deleted it. Reload the list.' });
      setBusy(row.id, false);
      return;
    }
    await logAudit('update', 'journal_prompt', `Edited journal prompt #${row.id}: ${saved[0].label}`, row.id);
    patchRow(row.id, saved[0]);
    setEditingId(null);
    setResult(row.id, { tone: 'ok', text: 'Saved. Past journal entries keep the words they were written with.' });
    setBusy(row.id, false);
  }

  async function toggle(row) {
    const next = !row.is_active;
    setBusy(row.id, true);
    setResult(row.id, null);
    const { data: saved, error } = await supabase
      .from('prompts')
      .update({ is_active: next })
      .eq('id', row.id)
      .select('id, is_active');
    if (error) {
      setResult(row.id, { tone: 'bad', text: describeSaveError(error) });
      setBusy(row.id, false);
      return;
    }
    if (!saved?.length) {
      setResult(row.id, { tone: 'warn', text: 'Not changed: the prompt was not found. Reload the list.' });
      setBusy(row.id, false);
      return;
    }
    await logAudit('update', 'journal_prompt', `${saved[0].is_active ? 'Activated' : 'Retired'} journal prompt #${row.id}: ${row.label}`, row.id);
    patchRow(row.id, { is_active: saved[0].is_active });
    setResult(row.id, { tone: 'ok', text: saved[0].is_active ? 'Activated. Readers are offered it again.' : 'Retired. Readers are no longer offered it. Past entries are untouched.' });
    setBusy(row.id, false);
  }

  async function remove(row) {
    // The button is only on when the use count says 0; the database checks again.
    setBusy(row.id, true);
    setResult(row.id, null);
    const { data: gone, error } = await supabase
      .from('prompts')
      .delete()
      .eq('id', row.id)
      .select('id');
    if (error) {
      setResult(row.id, { tone: 'bad', text: describeSaveError(error) });
      setConfirmId(null);
      setBusy(row.id, false);
      load();
      return;
    }
    if (!gone?.length) {
      setConfirmId(null);
      setBusy(row.id, false);
      setAddResult({ tone: 'warn', text: 'That prompt was already gone. The list has been reloaded.' });
      load();
      return;
    }
    await logAudit('delete', 'journal_prompt', `Deleted journal prompt #${row.id}: ${row.label}`, row.id);
    setRows(prev => prev.filter(r => r.id !== row.id));
    setConfirmId(null);
    setBusy(row.id, false);
    setAddResult({ tone: 'ok', text: 'Deleted.' });
  }

  async function move(row, dir) {
    const ids = presets.map(r => r.id);
    const next = movePreset(ids, row.id, dir);
    if (next === ids) return;
    setReordering(true);
    setAddResult(null);
    const { data: status, error } = await supabase.rpc('admin_reorder_prompts', { p_ids: next });
    if (error) {
      setResult(row.id, { tone: 'bad', text: `Not moved: ${error.message}` });
      setReordering(false);
      return;
    }
    if (status !== 'reordered') {
      // 'stale': the list changed under this screen, and the database moved nothing.
      setAddResult({ tone: 'warn', text: status === 'too_many' ? 'Not moved: there are too many prompts to number.' : 'Not moved: the list changed since you opened it. It has been reloaded, so try again.' });
      setReordering(false);
      load();
      return;
    }
    await logAudit('update', 'journal_prompt', `Reordered journal prompts: moved #${row.id} ${row.label} ${dir < 0 ? 'up' : 'down'}`, row.id);
    setRows(prev => prev.map(r => { const pos = next.indexOf(r.id); return pos >= 0 ? { ...r, sort_order: (pos + 1) * 10 } : r; }));
    setReordering(false);
  }

  const rowProps = (row, i, count) => ({
    row, index: i, count, usage, isMobile,
    busy: busyIds.has(row.id) || reordering,
    result: results[row.id],
    editing: editingId === row.id,
    confirming: confirmId === row.id,
    onMove: move,
    onStartEdit: r => { setEditingId(r.id); setConfirmId(null); setResult(r.id, null); },
    onCancelEdit: () => setEditingId(null),
    onSaveEdit: saveEdit,
    onToggle: toggle,
    onAskDelete: r => { setConfirmId(r.id); setEditingId(null); setResult(r.id, null); },
    onCancelDelete: () => setConfirmId(null),
    onDelete: remove,
  });

  const card = { background: 'var(--brand-white)', border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-lg)', padding: isMobile ? '14px 12px' : '18px 22px', marginBottom: 18 };
  const eyebrow = { fontSize: 'var(--font-size-xs)', fontWeight: 700, color: 'var(--neutral-500)', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6 };
  const formCheck = checkPromptFields(form);

  return (
    <div style={{ padding: isMobile ? 16 : '30px 36px', fontFamily: 'var(--font-body)', maxWidth: 900 }}>
      <div style={{ marginBottom: 16 }}>
        <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 26, fontWeight: 700, color: 'var(--neutral-800)', letterSpacing: -0.6, lineHeight: 1.1, margin: 0 }}>Journal Prompts</h1>
        <p style={{ fontSize: 'var(--font-size-base)', color: 'var(--neutral-500)', marginTop: 4 }}>
          What a reader can pick when they journal a book, in the order they see it. Editing or retiring a prompt changes what readers are offered from now on. Entries already written keep their words.
        </p>
      </div>

      {loading && <p role="status" style={{ color: 'var(--neutral-400)', fontSize: 'var(--font-size-sm)' }}>Loading…</p>}

      {!loading && loadError && (
        <div role="alert" style={{ padding: '12px 14px', borderRadius: 'var(--radius-md)', background: TONE.bad.bg, color: TONE.bad.color, border: `1px solid ${TONE.bad.border}`, fontSize: 'var(--font-size-sm)', fontWeight: 600 }}>
          The prompts could not be read: {loadError}{' '}
          <button type="button" onClick={load} style={{ fontFamily: 'inherit', cursor: 'pointer' }}>Try again</button>
        </div>
      )}

      {!loading && !loadError && (
        <>
          {usageError && (
            <div role="alert" data-usage-error="true" style={{ marginBottom: 14, padding: '10px 12px', borderRadius: 'var(--radius-md)', background: TONE.warn.bg, color: TONE.warn.color, border: `1px solid ${TONE.warn.border}`, fontSize: 'var(--font-size-sm)', fontWeight: 600 }}>
              The use counts could not be read ({usageError}), so no prompt can be deleted until they can.{' '}
              <button type="button" onClick={load} style={{ fontFamily: 'inherit', cursor: 'pointer' }}>Try again</button>
            </div>
          )}

          {/* What readers see */}
          <div style={card}>
            <div style={eyebrow}>What readers are offered</div>
            {showing.length === 0 ? (
              <div role="status" style={{ color: TONE.warn.color, fontSize: 'var(--font-size-sm)', fontWeight: 600 }}>Nothing is showing.</div>
            ) : (
              <ol aria-label="Prompts readers are offered" style={{ margin: 0, paddingLeft: 22, display: 'flex', flexDirection: 'column', gap: 4 }}>
                {showing.map(r => <li key={r.id} data-offered={r.id} style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-700)' }}><Chip row={r} /></li>)}
              </ol>
            )}
            <p style={{ margin: '10px 0 0', fontSize: 'var(--font-size-xs)', color: 'var(--neutral-400)' }}>
              {presets.length} {presets.length === 1 ? 'prompt' : 'prompts'} and the two fixed entries · {presets.filter(r => !r.is_active).length} retired.
            </p>
          </div>

          {/* Add */}
          <div style={card}>
            <div style={eyebrow}>Add a prompt</div>
            <p style={{ margin: '0 0 10px', fontSize: 'var(--font-size-sm)', color: 'var(--neutral-500)', lineHeight: 1.5 }}>
              Write it the way the others read, for example &ldquo;a book that made me cry&rdquo;. It goes at the end of the list.
            </p>
            <Fields value={form} onChange={setForm} isMobile={isMobile} idPrefix="add" />
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginTop: 10 }}>
              <Btn primary isMobile={isMobile} disabled={adding || !formCheck.ok} onClick={addOne}>{adding ? 'Adding…' : 'Add prompt'}</Btn>
              {!formCheck.ok && (form.label || form.emoji) && <span style={{ fontSize: 'var(--font-size-xs)', color: TONE.warn.color, fontWeight: 700 }}>{formCheck.text}</span>}
            </div>
            <Note result={addResult} />
          </div>

          {/* The list */}
          <div style={eyebrow}>Prompts, in the order readers see them</div>
          {presets.length === 0
            ? <div style={{ padding: '24px 0', textAlign: 'center', color: 'var(--neutral-400)', fontStyle: 'italic' }}>No prompts yet. Add one above.</div>
            : presets.map((r, i) => <PromptRow key={r.id} {...rowProps(r, i, presets.length)} />)}

          <div style={{ ...eyebrow, marginTop: 22 }}>Fixed entries</div>
          <p style={{ margin: '0 0 10px', fontSize: 'var(--font-size-sm)', color: 'var(--neutral-500)', lineHeight: 1.5 }}>
            Always last, and the journal needs both: &ldquo;type your own&rdquo; and &ldquo;I just want to talk about a book&rdquo;. You can change how they look. They can&apos;t be moved, retired or deleted.
          </p>
          {fixed.map((r, i) => <PromptRow key={r.id} {...rowProps(r, i, fixed.length)} />)}
        </>
      )}
    </div>
  );
}
